import { Controller } from "@hotwired/stimulus"

/**
 * PasswordResetController
 * 
 * Handles sending password reset emails via AJAX and displays feedback to the user.
 * Used by admins to send password reset emails to users from the user management page.
 * 
 * @example Usage in a button
 *   <button type="button"
 *           data-controller="password-reset"
 *           data-password-reset-url-value="/profile/users/123/send_password_reset"
 *           data-action="click->password-reset#send">
 *     <i class="fa-solid fa-key margin-right-5" aria-hidden="true"></i>
 *     Send Password Reset
 *   </button>
 */
export default class extends Controller {
  static values = {
    url: String
  }

  async send(event) {
    event.preventDefault()
    
    const button = this.element
    const originalContent = button.innerHTML
    
    button.disabled = true
    button.innerHTML = '<i class="fa-solid fa-spinner fa-spin margin-right-5" aria-hidden="true"></i> Sending...'

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        }
      })

      const data = await response.json()
      
      if (response.ok && data.success) {
        this.showFlashMessage(data.message, 'success')
      } else {
        this.showFlashMessage(data.message || 'Failed to send password reset email', 'danger')
      }
    } catch (error) {
      console.error('Password reset error:', error)
      this.showFlashMessage('An error occurred while sending the password reset email', 'danger')
    } finally {
      button.disabled = false
      button.innerHTML = originalContent
    }
  }

  // Built as nodes with textContent rather than interpolated into innerHTML. The
  // message carries a person's name now - invitation_success names who was resent -
  // and a name is the one part of this string somebody outside TECHFLEX chooses. Sent
  // through innerHTML it is markup, and it runs in the admin's browser. Same rule the
  // confirm dialog already follows; this was the path that still had to learn it.
  showFlashMessage(message, type) {
    const container = document.getElementById('flash-messages')
    if (!container) return

    const flashClass = type === 'success' ? 'success' : 'red'

    const column = document.createElement('div')
    column.className = 'col-12 null-space'

    const borderAbove = document.createElement('div')
    borderAbove.className = 'pseudo-border'

    const heading = document.createElement('h2')
    heading.className = `text-center ${flashClass} py-3 my-0`
    heading.textContent = message

    const borderBelow = document.createElement('div')
    borderBelow.className = 'pseudo-border d-xl-none'

    column.append(borderAbove, heading, borderBelow)
    container.replaceChildren(column)
  }
}
